
import { memo } from 'react';
import { 
    RefreshCw, 
    AlertTriangle, 
    Clock, 
    Heart, 
    Braces, 
    FileOutput, 
    FileInput,
    SlidersHorizontal 
} from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { type AslState, type BadgeType } from '@/types/workflow-graph';
import { cn } from '@/lib/utils';

interface StateBadgesProps {
    state: AslState;
    className?: string;
}

interface BadgeConfig {
    type: BadgeType;
    icon: React.ReactNode;
    label: string;
    tooltip: string;
    color: string;
    bgColor: string;
    /** Raw parameters object for rich tooltip rendering */
    rawParams?: Record<string, unknown>;
}

/**
 * Extracts badges from an ASL state definition.
 * Badges show configured features like Retry, Catch, Timeout, etc.
 */
function extractBadges(state: AslState): BadgeConfig[] {
    const badges: BadgeConfig[] = [];

    // Retry configuration
    if (state.Retry && state.Retry.length > 0) {
        const totalAttempts = state.Retry.reduce(
            (sum, r) => sum + (r.MaxAttempts ?? 3),
            0
        );
        badges.push({
            type: 'retry',
            icon: <RefreshCw size={10} />,
            label: `Retry x${totalAttempts}`,
            tooltip: `${state.Retry.length} retry config(s), max ${totalAttempts} attempts`,
            color: 'text-blue-700',
            bgColor: 'bg-blue-100',
        });
    }

    // Catch configuration (error handling)
    if (state.Catch && state.Catch.length > 0) {
        const errorTypes = state.Catch.flatMap(c => c.ErrorEquals).slice(0, 2);
        badges.push({
            type: 'catch',
            icon: <AlertTriangle size={10} />,
            label: 'Catch',
            tooltip: `Catches: ${errorTypes.join(', ')}${state.Catch.length > 2 ? '...' : ''}`,
            color: 'text-red-700',
            bgColor: 'bg-red-100',
        });
    }

    // Timeout (Task only)
    if (state.TimeoutSeconds || state.TimeoutSecondsPath) {
        const timeout = state.TimeoutSeconds 
            ? `${state.TimeoutSeconds}s` 
            : state.TimeoutSecondsPath;
        badges.push({
            type: 'timeout',
            icon: <Clock size={10} />,
            label: `Timeout`,
            tooltip: `Timeout: ${timeout}`,
            color: 'text-orange-700',
            bgColor: 'bg-orange-100',
        });
    }

    // Heartbeat (Task only)
    if (state.HeartbeatSeconds || state.HeartbeatSecondsPath) {
        const heartbeat = state.HeartbeatSeconds 
            ? `${state.HeartbeatSeconds}s` 
            : state.HeartbeatSecondsPath;
        badges.push({
            type: 'heartbeat',
            icon: <Heart size={10} />,
            label: `HB`,
            tooltip: `Heartbeat: ${heartbeat}`,
            color: 'text-pink-700',
            bgColor: 'bg-pink-100',
        });
    }

    // Parameters (data transform)
    if (state.Parameters && Object.keys(state.Parameters).length > 0) {
        // Format the full Parameters object as pretty-printed JSON
        const formattedParams = JSON.stringify(state.Parameters, null, 2);
        badges.push({
            type: 'parameters',
            icon: <Braces size={10} />,
            label: 'Params',
            tooltip: formattedParams,
            color: 'text-purple-700',
            bgColor: 'bg-purple-100',
            // Store the raw params for rich tooltip rendering
            rawParams: state.Parameters,
        });
    }

    // ResultSelector (data transform)
    if (state.ResultSelector && Object.keys(state.ResultSelector).length > 0) {
        badges.push({
            type: 'resultSelector',
            icon: <SlidersHorizontal size={10} />,
            label: 'Select',
            tooltip: 'Result selector configured',
            color: 'text-indigo-700',
            bgColor: 'bg-indigo-100',
        });
    }

    // ResultPath (data transform)
    if (state.ResultPath) {
        badges.push({
            type: 'resultPath',
            icon: <FileOutput size={10} />,
            label: 'Result',
            tooltip: `ResultPath: ${state.ResultPath}`,
            color: 'text-cyan-700',
            bgColor: 'bg-cyan-100',
        });
    }

    // InputPath (data transform)
    if (state.InputPath && state.InputPath !== '$') {
        badges.push({
            type: 'inputPath',
            icon: <FileInput size={10} />,
            label: 'Input',
            tooltip: `InputPath: ${state.InputPath}`,
            color: 'text-teal-700',
            bgColor: 'bg-teal-100',
        });
    }

    // OutputPath (data transform)
    if (state.OutputPath && state.OutputPath !== '$') {
        badges.push({
            type: 'outputPath',
            icon: <FileOutput size={10} />,
            label: 'Output',
            tooltip: `OutputPath: ${state.OutputPath}`,
            color: 'text-emerald-700',
            bgColor: 'bg-emerald-100',
        });
    }

    return badges;
}

/**
 * StateBadges - Displays feature badges for an ASL state.
 * Shows Retry, Catch, Timeout, Heartbeat, and data transform configs.
 */
export const StateBadges = memo(function StateBadges({
    state,
    className,
}: StateBadgesProps) {
    const badges = extractBadges(state);

    if (badges.length === 0) {
        return null;
    }

    // Limit to 4 badges + overflow
    const visibleBadges = badges.slice(0, 4);
    const overflowCount = badges.length - 4;

    return (
        <TooltipProvider>
            <div className={cn('flex flex-wrap gap-1', className)}>
                {visibleBadges.map((badge) => (
                    <Tooltip key={badge.type}>
                        <TooltipTrigger asChild>
                            <span
                                className={cn(
                                    'inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[9px] font-medium',
                                    badge.bgColor,
                                    badge.color
                                )}
                            >
                                {badge.icon}
                                <span className="ml-0.5">{badge.label}</span>
                            </span>
                        </TooltipTrigger>
                        <TooltipContent 
                            side="bottom" 
                            className={cn(
                                'text-xs',
                                badge.rawParams && 'max-w-md max-h-80 overflow-auto p-0'
                            )}
                        >
                            {badge.rawParams ? (
                                <div className="p-2">
                                    <div className="font-semibold text-slate-600 mb-1.5 text-[10px] uppercase tracking-wide">
                                        Parameters
                                    </div>
                                    <pre className="text-[10px] font-mono bg-slate-900 text-slate-100 p-2 rounded overflow-auto max-h-64 whitespace-pre-wrap break-all">
                                        {badge.tooltip}
                                    </pre>
                                </div>
                            ) : (
                                badge.tooltip
                            )}
                        </TooltipContent>
                    </Tooltip>
                ))}
                {overflowCount > 0 && (
                    <Tooltip>
                        <TooltipTrigger asChild>
                            <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-medium bg-slate-100 text-slate-600">
                                +{overflowCount}
                            </span>
                        </TooltipTrigger>
                        <TooltipContent side="bottom" className="text-xs">
                            {badges.slice(4).map(b => b.label).join(', ')}
                        </TooltipContent>
                    </Tooltip>
                )}
            </div>
        </TooltipProvider>
    );
});

// Export badge extraction for use in other components
export { extractBadges };

